import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Platform, View } from 'react-native';
import Toast from 'react-native-toast-message';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';
import { z } from 'zod';

import type { AuthTokenResponse } from '@/api/customers/use-create-customer';
import { useLoginCustomer } from '@/api/customers/use-login-customer';
import type { LoginFormProps } from '@/components/login-form';
import Navbar from '@/components/login-modal/navbar';
import { signIn } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { Button, ControlledInput, Text } from '@/ui';
import { Eon } from '@/ui/icons/eon';
import { Loader } from '@/ui/loader';

import ForgotPassword from '../forgot-password';

const emailFormSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8, 'Minimum 8 characters'),
});

export type EmailFormType = z.infer<typeof emailFormSchema>;

interface SigninEmailFormProps {
  onBack: () => void;
  onDone: (setupComplete: boolean) => void;
}

export default function SigninEmailForm({
  onBack,
  onDone,
}: SigninEmailFormProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Sign In Email',
    });
  }, []);

  const {
    mutate: loginCustomer,
    isPending: isSigningIn,
    error,
  } = useLoginCustomer();

  const {
    handleSubmit,
    control,
    trigger,
    setValue,
    formState: { isValid },
  } = useForm<EmailFormType>({
    resolver: zodResolver(emailFormSchema),
  });

  const handleContinue = (data: LoginFormProps) => {
    mixpanelService.track('Button Clicked', {
      'Button Name': 'Sign In Email',
    });

    loginCustomer(
      { ...data },
      {
        onSuccess: (respData: AuthTokenResponse) => {
          mixpanelService.track('Sign In Successful');

          Toast.show({
            text1: 'Logged in successfully',
            type: 'success',
          });

          signIn(respData.data.token);

          onDone(respData.data.setupComplete);
        },
        onError: () => {
          mixpanelService.track('Sign In Failed');
        },
      }
    );
  };

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
  };

  if (isSigningIn) {
    return <Loader />;
  }

  const renderBody = () => {
    if (showForgotPassword) {
      return (
        <View
          style={{
            flexGrow: 1,
            backgroundColor: theme.colors.background_2,
            borderRadius: 16,
          }}
        >
          <ForgotPassword
            onBack={() => setShowForgotPassword(false)}
            onDone={() => {
              setShowForgotPassword(false);
            }}
          />
        </View>
      );
    }

    return (
      <View
        style={{
          flexGrow: 1,
          backgroundColor: theme.colors.background_2,
          borderRadius: 16,
        }}
      >
        <Navbar
          type="back_with_text"
          onPress={onBack}
          buttonStyle={{ alignSelf: 'flex-start' }}
        />

        <View style={styles.container}>
          <View style={styles.formContainer}>
            {Platform.OS !== 'web' && <Eon width="48" height="18" />}

            <Text tx="emailForm.subtitle" type="title1" />

            <View style={styles.inputsContainer}>
              <ControlledInput
                control={control}
                name="email"
                label="Email"
                spellCheck={false}
                onBlur={() => {
                  trigger('email');
                }}
                onChangeText={(text) => {
                  trigger('email');
                  setValue('email', text);
                }}
                autoComplete="email"
              />

              <ControlledInput
                control={control}
                name="password"
                label="Password"
                secureTextEntry
                onBlur={() => {
                  trigger('password');
                }}
                onChangeText={(text) => {
                  trigger('password');
                  setValue('password', text);
                }}
              />
            </View>

            <View style={styles.buttonContainer}>
              <Button
                tx="emailForm.continue"
                type="primary"
                onPress={handleSubmit(handleContinue)}
                disabled={isValid === false}
              />

              {error ? (
                <Text type="bodyRegular" style={{ color: theme.colors.error }}>
                  {error.message}
                </Text>
              ) : null}

              <Button
                tx="emailForm.forgotPassword"
                type="ghost"
                onPress={handleForgotPassword}
              />
            </View>
          </View>
        </View>
      </View>
    );
  };

  return (
    <View style={{ flexGrow: 1, marginTop: UnistylesRuntime.insets.top }}>
      {renderBody()}
    </View>
  );
}

const stylesheet = createStyleSheet(() => ({
  container: {
    flexGrow: 1,
    paddingLeft: 24,
    paddingRight: 24,
    paddingBottom: 24,
    gap: 16,
    marginTop: 16,
  },
  formContainer: {
    gap: 16,
  },
  inputsContainer: {
    gap: 16,
  },
  buttonContainer: {
    gap: 16,
    alignItems: 'center',
  },
}));
